// Updated App.jsx with Layout fix
import 'react-toastify/dist/ReactToastify.css';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import {
  BrowserRouter, Navigate, Route, Routes,
} from 'react-router-dom';
import { useState, useEffect } from 'react';
import AuthRoute from './components/AuthRoute';
import ProtectedRoute from './components/ProtectedRoute';
import RoleGuard from './components/RoleGuard';
import HomePageEnhanced from './pages/HomePageEnhanced';
import SignupPage from './pages/SignupPage';
import SigninPage from './pages/SigninPage';
import OAuthRedirect from './pages/OAuthRedirect';
import FormBuilder from './pages/FormBuilder';
import AgenticFlowBuilderMCP from './pages/AgenticFlowBuilderMCP';
import FormRuntimePage from './pages/FormRuntimePage';
import { NavigationProvider } from './components/Navigation/NavigationContext';
import { RoleProvider } from './contexts/RoleContext';
import Dashboard from './pages/Dashboard';
import HelpSupport from './pages/HelpSupport';
import FormAnalytics from './pages/FormAnalytics';
import Settings from './pages/Settings';
import DataSources from './pages/DataSources';
import Analytics from './pages/Analytics';
import AgentManager from './components/AgentManager';
import AgentGallery from './components/AgentGallery';
import CreateAgent from './components/CreateAgent';
import AgentGroups from './components/AgentGroups';
import Security from './components/Security';
import Performance from './components/Performance';
import PublishedForms from './pages/PublishedForms';
import DraftForms from './pages/DraftForms';
import FormAnalyticsPage from './pages/FormAnalyticsPage';
import HelpSupportPage from './pages/HelpSupportPage';
import AgenticWorkflowHelp from './pages/AgenticWorkflowHelp';
import NodeDocumentationHub from './pages/NodeDocumentationHub';
import MCPHelp from './pages/MCPHelp';
import UserManagement from './components/UserManagement';
import RagDashboard from './components/rag/RagDashboard';
import TenantManagement from './pages/TenantManagement';
import LandingPage from './pages/LandingPage';
import DemoPage from './pages/DemoPage';
import ContactPage from './pages/ContactPage';
import PatientsPage from './pages/medical/PatientsPage';
import AppointmentsPage from './pages/medical/AppointmentsPage';
import ContactsPage from './pages/crm/ContactsPage';
import PropertiesPage from './pages/crm/PropertiesPage';
import OpportunitiesPage from './pages/crm/OpportunitiesPage';
import TasksPage from './pages/crm/TasksPage';
import NotesPage from './pages/crm/NotesPage';
import LeadStagesPage from './pages/crm/LeadStagesPage';
import NotificationsPage from './pages/crm/NotificationsPage';
import AiFieldsPage from './pages/crm/AiFieldsPage';
import CampaignsPage from './pages/crm/CampaignsPage';
import CrmDashboard from './pages/CrmDashboard';
import Layout from './components/Layout';
import PendingApprovalPage from './pages/PendingApprovalPage';
import TenantSignupPage from './pages/TenantSignupPage';
import TenantApprovalPage from './pages/TenantApprovalPage';
import LeadQualificationBotDemo from './pages/LeadQualificationBotDemo';
import ZoningCheckPage from './pages/ZoningCheckPage';
import CitizenPermitHelpPage from './pages/CitizenPermitHelpPage';
import CitizenAdvisorHostPage from './pages/studio/CitizenAdvisorHostPage';
import PermitAnalyticsPage from './pages/PermitAnalyticsPage';
import PortalPermitPage from './pages/PortalPermitPage';
import CrmSolutionPage from './pages/solutions/CrmSolutionPage';
import AutomationDashboard from './pages/AutomationDashboard';
import WorkflowTemplates from './components/Automation/WorkflowTemplates';
import ScheduledFlows from './components/Automation/ScheduledFlows';
import AutomationRules from './components/Automation/AutomationRules';
import UserProfile from './pages/UserProfile';
import FlowExecutionTracker from './pages/FlowExecutionTracker';
import ChatWidgetDemo from './pages/ChatWidgetDemo';
import MCPDashboardPage from './pages/MCPDashboardPage';
import DashboardBuilderPage from './pages/DashboardBuilderPage';
import AppBuilderEditPage from './pages/AppBuilderEditPage';
import DashboardRuntimePage from './pages/DashboardRuntimePage';
import AppGalleryPage from './pages/AppGalleryPage';
import DashboardPublishPage from './pages/DashboardPublishPage';
import DeenCompanionIntro from './pages/apps/DeenCompanionIntro';
import StandupWorkoutsIntro from './pages/apps/StandupWorkoutsIntro';
import TadabburIntro from './pages/apps/TadabburIntro';
import QuranPrivacyPage from './pages/quran/QuranPrivacyPage';
import QuranTermsPage from './pages/quran/QuranTermsPage';
import RootRoute from './components/RootRoute';
import { getBrand } from './config/brand';
import DomainRouteGate from './components/DomainRouteGate';
import V2StudioRouteGate from './components/V2StudioRouteGate';
import StudioHomePage from './pages/studio/StudioHomePage';
import StudioHelpPage from './pages/studio/StudioHelpPage';
import StudioFlowsListPage from './pages/studio/StudioFlowsListPage';
import StudioFlowEditPage from './pages/studio/StudioFlowEditPage';
import StudioAppsListPage from './pages/studio/StudioAppsListPage';
import StudioAppEditPage from './pages/studio/StudioAppEditPage';
import StudioV2PublishPage from './pages/studio/StudioV2PublishPage';
import StudioV2PublicDemoPage from './pages/studio/StudioV2PublicDemoPage';
import StudioV2PermitDemoPage from './pages/studio/StudioV2PermitDemoPage';
import StudioV2PermitPortalPage from './pages/studio/StudioV2PermitPortalPage';
import StudioV2PermitStaffPage from './pages/studio/StudioV2PermitStaffPage';
import StudioV2IntakeDemoPage from './pages/studio/StudioV2IntakeDemoPage';
import {
  StudioV2ReturnsDemoPage,
  StudioV2PersonalDemoPage,
  StudioV2DeskDemoPage,
  StudioV2RealEstateDemoPage,
  StudioV2ListingsDemoPage,
  StudioV2RecruitingAdvisorDemoPage,
} from './pages/studio/StudioV2AssistantDemoPage';
import StudioFormsPage from './pages/studio/StudioFormsPage';
import StudioFormsDesignPage from './pages/studio/StudioFormsDesignPage';
import StudioTemplatesPage from './pages/studio/StudioTemplatesPage';
import StudioExtensionsPage from './pages/studio/StudioExtensionsPage';
import StudioConnectPage from './pages/studio/StudioConnectPage';

function App() {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      ...(darkMode ? {
        background: { default: '#121212', paper: '#1e1e1e' },
        text: { primary: '#ffffff', secondary: '#b3b3b3' },
        primary: { main: '#90caf9' },
        divider: 'rgba(255, 255, 255, 0.12)',
      } : {
        background: { default: '#fafafa', paper: '#ffffff' },
        text: { primary: '#000000', secondary: '#666666' },
        primary: { main: '#1976d2' },
        divider: 'rgba(0, 0, 0, 0.12)',
      }),
    },
  });

  const handleToggleTheme = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
    window.dispatchEvent(new CustomEvent('themeChanged', { detail: { darkMode: newDarkMode } }));
  };

  const handleLogout = async () => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/logout`, {
        method: 'GET', credentials: 'include',
      });
    } catch (err) { console.error('Logout error:', err); }
    localStorage.removeItem('tkn');
    localStorage.removeItem('user');
    window.location.href = '/signin';
  };

  useEffect(() => {
    if (window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches) {
      const onClick = () => {
        window.scrollTo(0, 0);
        document.body.focus();
      };
      window.addEventListener('click', onClick, true);
      return () => window.removeEventListener('click', onClick, true);
    }
  }, []);

  useEffect(() => {
    document.title = getBrand() === 'interviewflow'
      ? 'InterviewFlow – Guided interviews for hiring, permits, and CRM'
      : 'BaigLabs – Products and platforms';
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer theme={darkMode ? 'dark' : 'light'} autoClose={3000} />
      <BrowserRouter>
        <RoleProvider>
          <NavigationProvider>
            <Box sx={{
              height: 'calc(var(--vh, 1vh) * 100)', overflow: 'auto', display: 'flex', flexDirection: 'column',
            }}
            >
              <Routes>
                <Route path="/" element={<RootRoute darkMode={darkMode} onToggleTheme={handleToggleTheme} />} />
                <Route path="/landing" element={<LandingPage darkMode={darkMode} onToggleTheme={handleToggleTheme} />} />
                <Route path="/demo" element={<DemoPage />} />
                <Route path="/solutions/crm" element={<CrmSolutionPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/signin" element={<SigninPage />} />
                <Route path="/login" element={<SigninPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/tenant-signup" element={<TenantSignupPage />} />
                <Route path="/pending-approval" element={<PendingApprovalPage />} />
                <Route path="/oauth-redirect" element={<OAuthRedirect />} />
                <Route path="/lead-qualification-bot-demo" element={<LeadQualificationBotDemo />} />
                <Route path="/zoning-check" element={<ZoningCheckPage />} />
                <Route path="/help/permits" element={<CitizenPermitHelpPage />} />
                {/* Legacy OpenCounter portals → V2 portal shells (same CLAW skill) */}
                <Route path="/portal/permit-discovery" element={<Navigate to="/v2/demo/permit/business" replace />} />
                <Route path="/portal/residential" element={<Navigate to="/v2/demo/permit/residential" replace />} />
                <Route path="/portal/events" element={<Navigate to="/v2/demo/permit/events" replace />} />
                <Route path="/portal/:portal" element={<PortalPermitPage />} />
                <Route path="/apps/deen-companion" element={<DeenCompanionIntro />} />
                <Route path="/apps/standup-workouts" element={<StandupWorkoutsIntro />} />
                <Route path="/apps/tadabbur" element={<TadabburIntro />} />
                <Route path="/quran/privacy" element={<QuranPrivacyPage />} />
                <Route path="/quran/terms" element={<QuranTermsPage />} />
                <Route
                  path="/p/:id"
                  element={(
                    <DomainRouteGate requireLegacy>
                      <DashboardPublishPage darkMode={darkMode} />
                    </DomainRouteGate>
                  )}
                />
                <Route path="/v2/p/:id" element={<StudioV2PublishPage />} />
                <Route path="/v2/demo/recruiting" element={<StudioV2RecruitingAdvisorDemoPage />} />
                <Route path="/v2/demo/recruiting/fixtures" element={<StudioV2PublicDemoPage />} />
                <Route path="/v2/demo/returns" element={<StudioV2ReturnsDemoPage />} />
                <Route path="/v2/demo/personal" element={<StudioV2PersonalDemoPage />} />
                <Route path="/v2/demo/desk" element={<StudioV2DeskDemoPage />} />
                <Route path="/v2/demo/realestate" element={<StudioV2RealEstateDemoPage />} />
                <Route path="/v2/demo/listings" element={<StudioV2ListingsDemoPage />} />
                <Route path="/v2/demo/permit" element={<StudioV2PermitDemoPage />} />
                <Route path="/v2/demo/permit/staff" element={<StudioV2PermitStaffPage />} />
                <Route path="/v2/demo/permit/intake" element={<StudioV2IntakeDemoPage />} />
                <Route
                  path="/v2/demo/permit/intake/classic"
                  element={<Navigate to="/v2/demo/permit/intake" replace />}
                />
                {/* classic → intake; query string preserved by browser when relative Navigate */}
                <Route path="/v2/demo/permit/:portal" element={<StudioV2PermitPortalPage />} />
                {/* Vanity city hosts: / → /advisor (RootRoute); package from resolve-host */}
                <Route path="/advisor" element={<CitizenAdvisorHostPage />} />
                <Route path="/advisor/:portal" element={<CitizenAdvisorHostPage />} />
                <Route path="/v2/demo" element={<Navigate to="/v2/demo/recruiting" replace />} />

                <Route element={<ProtectedRoute><Layout darkMode={darkMode} onToggleTheme={handleToggleTheme} onLogout={handleLogout} /></ProtectedRoute>}>
                  <Route path="/dashboard" element={<Dashboard darkMode={darkMode} />} />
                  <Route path="/submissions" element={<FormAnalyticsPage darkMode={darkMode} />} />
                  <Route path="/home" element={<HomePageEnhanced darkMode={darkMode} />} />
                  <Route path="/formBuilder" element={<FormBuilder darkMode={darkMode} />} />
                  <Route path="/form-builder" element={<Navigate to="/formBuilder" replace />} />
                  <Route path="/form-runtime" element={<FormRuntimePage darkMode={darkMode} />} />
                  <Route path="/forms/drafts" element={<DraftForms darkMode={darkMode} />} />
                  <Route path="/forms/published" element={<PublishedForms darkMode={darkMode} />} />
                  <Route path="/forms/analytics" element={<FormAnalyticsPage darkMode={darkMode} />} />
                  <Route
                    path="/agentFlowBuilder"
                    element={(
                      <DomainRouteGate requireLegacy>
                        <AgenticFlowBuilderMCP darkMode={darkMode} />
                      </DomainRouteGate>
                    )}
                  />
                  <Route
                    path="/agentic-flow-builder"
                    element={(
                      <DomainRouteGate requireLegacy>
                        <AgenticFlowBuilderMCP darkMode={darkMode} />
                      </DomainRouteGate>
                    )}
                  />
                  <Route path="/agent-manager" element={<AgentManager darkMode={darkMode} />} />
                  <Route path="/agents" element={<AgentGallery darkMode={darkMode} />} />
                  <Route path="/agents/create" element={<CreateAgent darkMode={darkMode} />} />
                  <Route path="/agent-groups" element={<AgentGroups darkMode={darkMode} />} />
                  <Route path="/rag-dashboard" element={<RagDashboard darkMode={darkMode} />} />
                  <Route path="/form/:formId" element={<FormRuntimePage darkMode={darkMode} />} />
                  <Route path="/form-analytics/:formId" element={<FormAnalytics darkMode={darkMode} />} />
                  <Route path="/analytics" element={<RoleGuard requiredPermission="analytics"><Analytics darkMode={darkMode} /></RoleGuard>} />
                  <Route path="/settings" element={<RoleGuard requiredPermission="settings"><Settings darkMode={darkMode} /></RoleGuard>} />
                  <Route path="/security" element={<RoleGuard requiredPermission="security"><Security darkMode={darkMode} /></RoleGuard>} />
                  <Route path="/performance" element={<RoleGuard requiredPermission="performance"><Performance darkMode={darkMode} /></RoleGuard>} />
                  <Route path="/data-sources" element={<RoleGuard requiredPermission="data-sources"><DataSources darkMode={darkMode} /></RoleGuard>} />
                  <Route path="/user-management" element={<RoleGuard requiredPermission="settings"><UserManagement darkMode={darkMode} /></RoleGuard>} />
                  <Route path="/tenants" element={<RoleGuard requiredPermission="settings"><TenantManagement /></RoleGuard>} />
                  <Route path="/tenant-approval" element={<RoleGuard requiredPermission="settings"><TenantApprovalPage /></RoleGuard>} />
                  <Route path="/help-dashboard" element={<HelpSupportPage darkMode={darkMode} onToggleTheme={handleToggleTheme} />} />
                  <Route path="/help" element={<HelpSupport darkMode={darkMode} />} />
                  <Route path="/support" element={<HelpSupport darkMode={darkMode} />} />
                  <Route path="/agentic-workflow-help" element={<AgenticWorkflowHelp darkMode={darkMode} />} />
                  <Route path="/node-documentation" element={<NodeDocumentationHub />} />
                  <Route path="/mcp-help" element={<MCPHelp darkMode={darkMode} />} />
                  <Route path="/medical/patients" element={<PatientsPage />} />
                  <Route path="/medical/appointments" element={<AppointmentsPage />} />
                  <Route path="/crm/contacts" element={<ContactsPage />} />
                  <Route path="/crm/properties" element={<PropertiesPage />} />
                  <Route path="/crm/opportunities" element={<OpportunitiesPage />} />
                  <Route path="/crm/tasks" element={<TasksPage />} />
                  <Route path="/crm/notes" element={<NotesPage />} />
                  <Route path="/crm/lead-stages" element={<LeadStagesPage />} />
                  <Route path="/crm/notifications" element={<NotificationsPage />} />
                  <Route path="/crm/ai-fields" element={<AiFieldsPage />} />
                  <Route path="/crm/campaigns" element={<CampaignsPage />} />
                  <Route path="/crm" element={<CrmDashboard />} />
                  <Route path="/workflow-templates" element={<WorkflowTemplates />} />
                  <Route path="/scheduled-flows" element={<ScheduledFlows />} />
                  <Route path="/automation-rules" element={<AutomationRules />} />
                  <Route path="/automation" element={<AutomationDashboard />} />
                  <Route path="/profile" element={<UserProfile />} />
                  <Route path="/flow-execution-tracker" element={<FlowExecutionTracker darkMode={darkMode} />} />
                  <Route path="/permit-analytics" element={<PermitAnalyticsPage darkMode={darkMode} />} />
                  <Route path="/chat-widget-demo" element={<ChatWidgetDemo />} />
                  <Route
                    path="/dashboard-builder"
                    element={(
                      <DomainRouteGate requireLegacy>
                        <DashboardBuilderPage darkMode={darkMode} />
                      </DomainRouteGate>
                    )}
                  />
                  <Route
                    path="/dashboard-builder/edit/:id"
                    element={(
                      <DomainRouteGate requireLegacy>
                        <AppBuilderEditPage darkMode={darkMode} />
                      </DomainRouteGate>
                    )}
                  />
                  <Route
                    path="/dashboard-builder/run/:id"
                    element={(
                      <DomainRouteGate requireLegacy>
                        <DashboardRuntimePage darkMode={darkMode} />
                      </DomainRouteGate>
                    )}
                  />
                  <Route path="/apps" element={<AppGalleryPage darkMode={darkMode} />} />
                  <Route path="/mcp-dashboard" element={<MCPDashboardPage />} />
                  <Route path="/mcp-dashboard/ai" element={<MCPDashboardPage />} />
                  <Route path="/mcp-dashboard/system" element={<MCPDashboardPage />} />
                  <Route path="/mcp-dashboard/billing" element={<MCPDashboardPage />} />
                  <Route path="/mcp-dashboard/monitoring" element={<MCPDashboardPage />} />
                  <Route path="/mcp-dashboard/settings" element={<MCPDashboardPage />} />
                  <Route path="/mcp-templates" element={<MCPDashboardPage />} />
                  <Route path="/mcp-analytics" element={<MCPDashboardPage />} />
                  <Route path="/mcp-tools" element={<MCPDashboardPage />} />
                  <Route path="/mcp-search" element={<MCPDashboardPage />} />
                  <Route path="/mcp-statistics" element={<MCPDashboardPage />} />

                  <Route
                    path="/studio"
                    element={(
                      <V2StudioRouteGate>
                        <StudioHomePage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/help"
                    element={(
                      <V2StudioRouteGate>
                        <StudioHelpPage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/help/:sectionId"
                    element={(
                      <V2StudioRouteGate>
                        <StudioHelpPage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/extensions"
                    element={(
                      <V2StudioRouteGate>
                        <StudioExtensionsPage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/connect"
                    element={(
                      <V2StudioRouteGate>
                        <StudioConnectPage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/templates"
                    element={(
                      <V2StudioRouteGate>
                        <StudioTemplatesPage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/flows"
                    element={(
                      <V2StudioRouteGate>
                        <StudioFlowsListPage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/flows/:id"
                    element={(
                      <V2StudioRouteGate>
                        <StudioFlowEditPage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/apps"
                    element={(
                      <V2StudioRouteGate>
                        <StudioAppsListPage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/apps/:id"
                    element={(
                      <V2StudioRouteGate>
                        <StudioAppEditPage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/forms"
                    element={(
                      <V2StudioRouteGate>
                        <StudioFormsPage />
                      </V2StudioRouteGate>
                    )}
                  />
                  <Route
                    path="/studio/forms/design"
                    element={(
                      <V2StudioRouteGate>
                        <StudioFormsDesignPage darkMode={darkMode} />
                      </V2StudioRouteGate>
                    )}
                  />
                </Route>
              </Routes>
            </Box>
          </NavigationProvider>
        </RoleProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
